import * as React from 'react';
import { cn } from '../../lib/utils';

interface UserPreferencesModalProps {
  children: React.ReactNode;
  className?: string;
}

export function UserPreferencesModal({ children, className }: UserPreferencesModalProps) {
  return (
    <div className={cn('flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden', className)}>
      {children}
    </div>
  );
}

interface BodyProps {
  children: React.ReactNode;
  className?: string;
}
function Body({ children, className }: BodyProps) {
  return (
    <div
      className={cn('min-h-0 flex-1 overflow-y-auto', className)}
      style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--primary)/0.25) transparent' }}
    >
      <div className="flex flex-col gap-4 pb-1">{children}</div>
    </div>
  );
}

interface SubHeadingProps {
  children: React.ReactNode;
  className?: string;
}
function SubHeading({ children, className }: SubHeadingProps) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-secondary">
        {children}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
    </div>
  );
}

interface HotkeysGridProps {
  children: React.ReactNode;
  className?: string;
}
function HotkeysGrid({ children, className }: HotkeysGridProps) {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {children}
    </div>
  );
}

// Maps key names to display labels
const KEY_DISPLAY: Record<string, string> = {
  arrowleft: '←',
  arrowright: '→',
  arrowup: '↑',
  arrowdown: '↓',
  escape: 'Esc',
  enter: '↵',
  backspace: '⌫',
  delete: 'Del',
  space: 'Space',
  tab: 'Tab',
  ctrl: 'Ctrl',
  shift: 'Shift',
  alt: 'Alt',
  meta: '⌘',
};

function formatKey(key: string): string {
  const lower = key.toLowerCase().trim();
  return KEY_DISPLAY[lower] ?? (key.charAt(0).toUpperCase() + key.slice(1));
}

function KeyChips({ keyString }: { keyString: string }) {
  const normalized = Array.isArray(keyString) ? keyString.join('+') : String(keyString ?? '');
  const parts = normalized ? normalized.split('+').filter(Boolean) : [];
  if (!parts.length) {
    return <span className="text-[12px] text-white/20">—</span>;
  }
  return (
    <div className="flex items-center gap-0.5">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span className="px-0.5 text-[9px] text-white/20">+</span>
          )}
          <kbd className="rounded-[5px] bg-accent px-[7px] py-[3px] text-[10px] font-mono font-medium leading-none text-white/60 shadow-[inset_0_-2px_0_rgba(0,0,0,0.4),0_1px_0_rgba(255,255,255,0.05)] ring-1 ring-white/[0.1]">
            {formatKey(part)}
          </kbd>
        </React.Fragment>
      ))}
    </div>
  );
}

interface HotkeyProps {
  label: string;
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  hotkeys?: {
    record: (callback: (sequence: string[]) => void) => void;
    pause: () => void;
    unpause: () => void;
    startRecording: () => void;
  };
}

function Hotkey({ label, placeholder, className, value, onChange, hotkeys }: HotkeyProps) {
  const [isRecording, setIsRecording] = React.useState(false);

  const onInputKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    hotkeys?.record((sequence: string[]) => {
      const keys = sequence.join('+');
      hotkeys?.unpause();
      setIsRecording(false);
      onChange?.(keys);
    });
  };

  const onFocus = () => {
    setIsRecording(true);
    hotkeys?.pause();
    hotkeys?.startRecording();
  };

  const onBlur = () => {
    setIsRecording(false);
    hotkeys?.unpause();
  };

  const rawValue = value ?? placeholder ?? '';
  const displayValue = Array.isArray(rawValue) ? rawValue.join('+') : String(rawValue);

  return (
    <div
      className={cn(
        'group relative flex cursor-pointer items-center justify-between gap-4 rounded-lg px-3 py-2.5 transition-all duration-150 hover:bg-white/[0.03]',
        isRecording && 'bg-card/50 ring-1 ring-primary/30',
        className
      )}
    >
      {/* Invisible full-row input for capturing key events */}
      <input
        className="absolute inset-0 cursor-pointer opacity-0"
        onKeyDown={onInputKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        readOnly
      />

      <span
        className={cn(
          'relative pointer-events-none text-[12px] transition-colors duration-150',
          isRecording ? 'text-white/80' : 'text-white/45 group-hover:text-white/65'
        )}
      >
        {label}
      </span>

      <div className="relative pointer-events-none flex items-center">
        {isRecording ? (
          <div className="flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1.5 ring-1 ring-primary/60 shadow-[0_0_14px_hsl(var(--primary)/0.3)]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-secondary" />
            <span className="text-[10px] font-semibold text-secondary">Recording…</span>
          </div>
        ) : (
          <KeyChips keyString={displayValue} />
        )}
      </div>
    </div>
  );
}

UserPreferencesModal.Body = Body;
UserPreferencesModal.HotkeysGrid = HotkeysGrid;
UserPreferencesModal.Hotkey = Hotkey;
UserPreferencesModal.SubHeading = SubHeading;
